import { ApiRequestError } from './unwrapApi';

/**
 * Default retry rule for every mutation (wired in main.tsx).
 *
 * A write is retried ONLY when `fetch` itself rejected with a `TypeError` (offline, DNS, connection dropped; Chrome
 * 'Failed to fetch', Safari 'Load failed' / 'The network connection was lost.' / 'The Internet connection appears to be
 * offline.' / 'cancelled', Firefox 'NetworkError when attempting to fetch resource') in code that calls `fetch` directly
 * (fleetFetch, complianceFetch, fetchWithAuth). Retries run up to 3 times (1s, 2s, 4s).
 *
 * Residual risk, stated plainly: a `fetch` TypeError means the browser got no answer, NOT that the server never saw the
 * request. Safari's 'The network connection was lost.' (and 'Load failed') can fire AFTER the server received and
 * processed the request, so retrying a create that has no idempotency key could post it twice. We accept that because
 * the common causes (offline, DNS, a dropped connection before the request is sent) are far more frequent, and a staff
 * member who sees a failed save would retry by hand anyway. If and when the API supports idempotency keys, creates
 * should send one so this retry becomes fully safe.
 *
 * Everything else is NOT retried:
 * - `ApiRequestError` (a refusal, a validation error or a timeout answered by `fetchApi`): the answer will not change,
 *   retrying only delays the message by about 7 seconds, and a retried create can be posted up to 4 times (no create
 *   endpoint has a duplicate-protection key).
 * - A plain `Error` (the older hand-rolled `if (!r.success) throw new Error(...)`, or a `!response.ok` thrown by a
 *   direct `fetch` helper): the server answered, so the failure is definite for the same reason.
 * - A failed dynamic `import()` of a code chunk (a TypeError whose message also contains 'Failed to fetch' or 'Load failed'):
 *   it is not a request of ours, and a mutation that already succeeded must not be posted again.
 * - Anything with an HTTP `status`, and any `TypeError` whose message is not a network failure (an ordinary code bug
 *   thrown after the write succeeded, such as "Cannot read properties of undefined": retrying could post a create again).
 */
/** What browsers put in the TypeError that `fetch` rejects with when the request never got an answer. */
const NETWORK_FAILURE =
  /failed to fetch|networkerror|load failed|network request failed|network connection was lost|internet connection appears to be offline|^\s*cancell?ed\.?\s*$/i;

/** A lazy-loaded code chunk that failed to download: its message can contain the phrases above, but no write was sent. */
const CHUNK_LOAD_FAILURE = /dynamically imported module|importing a module script failed/i;

export function shouldRetryMutation(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiRequestError) return false;
  if (error instanceof TypeError && !CHUNK_LOAD_FAILURE.test(error.message) && NETWORK_FAILURE.test(error.message)) {
    return failureCount < 3;
  }
  return false;
}
